import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, Validators} from '@angular/forms';
import {BookingStatusDTO, EngineSearchInvoiceService} from '@tj/tj-dmp-lib-api';
import {ConfirmationService, MenuItem} from 'primeng/api';
import {of, Subject} from 'rxjs';
import {catchError, finalize, switchMap, takeUntil} from 'rxjs/operators';
import {PagePermissionsService} from '../../../auth/page-permissions.service';

@Component({
    templateUrl: './booking-status.component.html', styleUrls: ['./booking-status.component.scss'],
    providers: [ConfirmationService], standalone: false
})
export class BookingStatusComponent implements OnInit, OnDestroy {
    readonly breadcrumb: MenuItem[] = [{label: 'Administration'}, {label: 'Statuts de réservation'}];
    readonly products = [{value: 'flight', label: 'Vols'}, {value: 'hotel', label: 'Hôtels'},
        {value: 'parking', label: 'Parkings'}, {value: 'car', label: 'Location de voiture'}];
    readonly sources = [{value: 'INITIAL', label: 'Initialisation'}, {value: 'DETECTED', label: 'Détection automatique'}, {value: 'MANUAL', label: 'Ajout manuel'}];
    private readonly loads$ = new Subject<string>();
    private readonly destroy$ = new Subject<void>();
    tenant: string;
    rows: BookingStatusDTO[] = [];
    loading = false;
    error = '';
    notice = '';
    search = '';
    productFilter = '';
    sourceFilter = '';
    activeFilter = '';
    needsDefinition = false;
    editorVisible = false;
    saving = false;
    deletingId: number | null = null;
    editorError = '';
    editing: BookingStatusDTO | null = null;
    private editingTenant: string;
    readonly form = this.fb.group({productType: ['flight', Validators.required],
        bookingStatus: ['', [Validators.required, Validators.pattern(/\S/), Validators.maxLength(100)]],
        label: ['', [Validators.required, Validators.pattern(/\S/), Validators.maxLength(255)]],
        definition: ['', Validators.maxLength(10000)], active: [true]});

    constructor(private readonly api: EngineSearchInvoiceService, private readonly fb: FormBuilder,
        private readonly confirmation: ConfirmationService, public readonly permissions: PagePermissionsService) {}

    ngOnInit(): void {
        this.loads$.pipe(switchMap(tenant => {
            this.loading = !!tenant; this.error = ''; this.rows = [];
            return tenant ? this.api.listBookingStatuses(tenant).pipe(catchError(() => {
                this.error = 'Impossible de charger les statuts de ce groupe. Réessayez.'; return of([]);
            })) : of([]);
        }), takeUntil(this.destroy$)).subscribe(rows => { this.rows = rows; this.loading = false; });
    }

    selectTenant(tenant: string): void {
        if (this.tenant !== tenant) {
            this.tenant = tenant; this.editorVisible = false; this.notice = ''; this.resetFilters(); this.reload();
        }
    }
    reload(): void { this.loads$.next(this.tenant); }
    resetFilters(): void { this.search = ''; this.productFilter = ''; this.sourceFilter = ''; this.activeFilter = ''; this.needsDefinition = false; }
    get filteredRows(): BookingStatusDTO[] {
        const query = this.normalize(this.search);
        return this.rows.filter(row => (!this.productFilter || row.productType === this.productFilter)
            && (!this.sourceFilter || row.source === this.sourceFilter)
            && (!this.activeFilter || row.active === (this.activeFilter === 'active'))
            && (!this.needsDefinition || !row.definition?.trim())
            && this.normalize([row.bookingStatus, row.label, row.definition, this.productLabel(row.productType)].join(' ')).includes(query));
    }
    private normalize(value: string): string { return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim(); }
    productLabel(code: string): string { return this.products.find(product => product.value === code)?.label || code; }
    observedDate(value: string): string {
        return new Intl.DateTimeFormat('fr-FR', {day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris'}).format(new Date(value));
    }
    sourceLabel(code: string): string { return this.sources.find(source => source.value === code)?.label || code; }

    edit(status: BookingStatusDTO | null = null): void {
        this.editing = status; this.editingTenant = this.tenant; this.editorError = '';
        this.form.reset({productType: status?.productType || this.productFilter || 'flight', bookingStatus: status?.bookingStatus || '',
            label: status?.label || '', definition: status?.definition || '', active: status?.active ?? true});
        if (status) { this.form.controls.productType.disable(); this.form.controls.bookingStatus.disable(); }
        else { this.form.controls.productType.enable(); this.form.controls.bookingStatus.enable(); }
        this.editorVisible = true;
    }

    save(): void {
        this.form.markAllAsTouched();
        if (this.form.invalid || this.saving || this.editingTenant !== this.tenant) { return; }
        const tenant = this.editingTenant;
        const value = this.form.getRawValue();
        const status = {...value, id: this.editing?.id, bookingStatus: value.bookingStatus.trim(), label: value.label.trim()};
        this.saving = true; this.editorError = '';
        this.api.saveBookingStatus(tenant, status).pipe(finalize(() => this.saving = false), takeUntil(this.destroy$)).subscribe({
            next: () => { if (tenant === this.tenant) { this.editorVisible = false; this.notice = 'Statut enregistré.'; this.reload(); } },
            error: error => { if (tenant === this.tenant) { this.editorError = error?.error?.title || error?.error?.detail || 'Impossible d’enregistrer le statut.'; } }
        });
    }

    confirmDelete(status: BookingStatusDTO): void {
        const tenant = this.tenant;
        this.confirmation.confirm({header: 'Supprimer un statut', message: `Supprimer ${status.bookingStatus} pour ${this.productLabel(status.productType)} ? Un statut utilisé par un appel ne peut pas être supprimé.`,
            acceptLabel: 'Supprimer', rejectLabel: 'Annuler', accept: () => {
                this.deletingId = status.id; this.error = '';
                this.api.deleteBookingStatus(tenant, status.id).pipe(finalize(() => this.deletingId = null), takeUntil(this.destroy$)).subscribe({
                    next: () => { if (tenant === this.tenant) { this.notice = 'Statut supprimé.'; this.reload(); } },
                    error: error => { if (tenant === this.tenant) { this.error = error?.error?.title || error?.error?.detail || 'Suppression impossible.'; } }
                });
            }});
    }
    ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); this.loads$.complete(); }
}
